import { useMemo } from "react";
import type { ImageClassificationResponse } from "@/services/api/files/fileUpload.types";
import { useTags } from "@/features/admin/queries/useTags";
import { DEFAULT_TAG_COLOR } from "@/services/api/tags/types";

interface ThumbnailDimensions {
  width: number | null;
  height: number | null;
}

export interface OriginalBoundingBox {
  tagName: string;
  width: number;
  height: number;
  confidence: number;
}

export interface ScaledBoundingBox extends OriginalBoundingBox {
  scaledX: number;
  scaledY: number;
  scaledWidth: number;
  scaledHeight: number;
}

interface ImageBoundingBoxesProps {
  classifications: ImageClassificationResponse[];
  imageWidth: number;
  imageHeight: number;
  displayWidth: number;
  displayHeight: number;
  largeThumbnailDimensions?: ThumbnailDimensions | null;
  minConfidence?: number | null;
}

function calculateScale(
  imageWidth: number,
  imageHeight: number,
  largeThumbnailDimensions?: ThumbnailDimensions | null
): { scaleX: number; scaleY: number } {
  if (
    !largeThumbnailDimensions ||
    !largeThumbnailDimensions.width ||
    !largeThumbnailDimensions.height
  ) {
    return { scaleX: 1, scaleY: 1 };
  }

  const scaleX = imageWidth / largeThumbnailDimensions.width;
  const scaleY = imageHeight / largeThumbnailDimensions.height;

  return { scaleX, scaleY };
}

function scaleBoundingBox(
  classification: ImageClassificationResponse,
  scaleX: number,
  scaleY: number
): ScaledBoundingBox {
  const centerX = classification.x;
  const centerY = classification.y;
  const originalWidth = classification.width;
  const originalHeight = classification.height;

  const scaledCenterX = centerX * scaleX;
  const scaledCenterY = centerY * scaleY;
  const scaledWidth = originalWidth * scaleX;
  const scaledHeight = originalHeight * scaleY;

  const scaledX = scaledCenterX - scaledWidth / 2;
  const scaledY = scaledCenterY - scaledHeight / 2;

  return {
    tagName: classification.tagName,
    width: originalWidth,
    height: originalHeight,
    confidence: classification.confidence,
    scaledX,
    scaledY,
    scaledWidth,
    scaledHeight,
  };
}

export function ImageBoundingBoxes({
  classifications,
  imageWidth,
  imageHeight,
  displayWidth,
  displayHeight,
  largeThumbnailDimensions,
  minConfidence,
}: ImageBoundingBoxesProps) {
  const { data: availableTags = [] } = useTags();

  const tagColorMap = useMemo(() => {
    const map = new Map<string, string>();
    availableTags.forEach((tag) => {
      if (tag.name) {
        map.set(tag.name, tag.color || DEFAULT_TAG_COLOR);
      }
    });
    return map;
  }, [availableTags]);

  const { scaleX, scaleY } = useMemo(
    () => calculateScale(imageWidth, imageHeight, largeThumbnailDimensions),
    [imageWidth, imageHeight, largeThumbnailDimensions]
  );

  const scaledClassifications = useMemo(() => {
    const scaled = classifications.map((classification) =>
      scaleBoundingBox(classification, scaleX, scaleY)
    );
    return scaled;
  }, [classifications, scaleX, scaleY]);

  const filteredClassifications = useMemo(() => {
    if (minConfidence === null || minConfidence === undefined) {
      return scaledClassifications;
    }
    if (minConfidence === 0.9) {
      return scaledClassifications.filter(
        (classification) => classification.confidence >= 0.9
      );
    }
    if (minConfidence === 0.5) {
      return scaledClassifications.filter(
        (classification) =>
          classification.confidence >= 0.5 && classification.confidence < 0.9
      );
    }
    if (minConfidence === -1) {
      return scaledClassifications.filter(
        (classification) => classification.confidence < 0.5
      );
    }
    return scaledClassifications;
  }, [scaledClassifications, minConfidence]);

  if (
    !classifications ||
    classifications.length === 0 ||
    !imageWidth ||
    !imageHeight
  ) {
    return null;
  }

  return (
    <svg
      className="absolute pointer-events-none"
      style={{
        width: displayWidth,
        height: displayHeight,
        top: 0,
        left: 0,
        pointerEvents: "none",
        overflow: "visible",
      }}
      viewBox={`0 0 ${imageWidth} ${imageHeight}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {filteredClassifications.map((classification, index) => {
        const color =
          tagColorMap.get(classification.tagName) || DEFAULT_TAG_COLOR;
        const opacity = Math.min(0.8, 0.3 + classification.confidence * 0.5);

        const { scaledX, scaledY, scaledWidth, scaledHeight } = classification;

        const uniqueKey = `${classification.tagName}-${scaledX}-${scaledY}-${scaledWidth}-${scaledHeight}-${classification.confidence}-${index}`;

        return (
          <g key={uniqueKey}>
            <rect
              x={scaledX}
              y={scaledY}
              width={scaledWidth}
              height={scaledHeight}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeOpacity={opacity}
              rx={2}
            />
            <rect
              x={scaledX}
              y={Math.max(0, scaledY - 28)}
              width={Math.max(140, classification.tagName.length * 10 + 60)}
              height={28}
              fill={color}
              fillOpacity={opacity}
              rx={2}
            />
            <text
              x={scaledX + 8}
              y={Math.max(20, scaledY - 8)}
              fill="white"
              fontSize={16}
              fontWeight="600"
              style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
            >
              {classification.tagName} (
              {(classification.confidence * 100).toFixed(1)}%)
            </text>
          </g>
        );
      })}
    </svg>
  );
}
